export const TEMPLATE_CHAR_LIMIT = 1024;

const REFRESH_CODES = new Set([
  'CAMPAIGN_NOT_OWNED',
  'CAMPAIGN_NOT_LIVE',
  'CAMPAIGN_PAUSED',
  'TEMPLATE_NOT_APPROVED',
]);

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

export function splitPlaceholders(text) {
  const source = String(text || '');
  const parts = [];
  const pattern = new RegExp(PLACEHOLDER.source, 'g');
  let last = 0;
  let match = pattern.exec(source);
  while (match) {
    if (match.index > last) parts.push({ kind: 'text', text: source.slice(last, match.index) });
    parts.push({ kind: 'slot', name: match[1] });
    last = match.index + match[0].length;
    match = pattern.exec(source);
  }
  if (last < source.length) parts.push({ kind: 'text', text: source.slice(last) });
  return parts;
}

export function fillPlaceholders(text, params) {
  return String(text || '').replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (full, name) => {
    const value = params?.[name];
    if (value == null || String(value).trim() === '') return full;
    return String(value);
  });
}

export function templateFields(campaign) {
  const declared = (Array.isArray(campaign?.params) ? campaign.params : [])
    .map((item) => ({
      name: String(item?.name || '').trim(),
      example: item?.example == null ? '' : String(item.example),
      max_length: item?.max_length == null || item?.max_length === '' ? null : item.max_length,
    }))
    .filter((item) => item.name);
  if (declared.length) return declared;

  const texts = [];
  if (campaign?.header?.format === 'text') texts.push(campaign.header.text);
  texts.push(campaign?.body);
  const fields = [];
  texts.forEach((text) => {
    splitPlaceholders(text).forEach((part) => {
      if (part.kind !== 'slot' || fields.some((field) => field.name === part.name)) return;
      fields.push({ name: part.name, example: '', max_length: null });
    });
  });
  return fields;
}

export function exampleValues(fields) {
  const values = {};
  (fields || []).forEach((field) => {
    const sample = String(field?.example || '').trim();
    if (sample) values[field.name] = sample;
  });
  return values;
}

export function finishedMessage(campaign, params) {
  const header = campaign?.header?.format === 'text' ? String(campaign.header?.text || '') : '';
  const body = fillPlaceholders(campaign?.body || '', params);
  return [header, body].filter((part) => part !== '').join('\n');
}

export function paramLabel(name) {
  const raw = String(name || '');
  if (raw === 'profile_name') return 'WhatsApp name';
  if (/^\d+$/.test(raw)) return `Value ${raw}`;
  const words = raw.split('_').filter(Boolean);
  if (!words.length) return raw;
  return words
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export function templateSendParams(fields, values) {
  const params = {};
  (fields || []).forEach((field) => {
    const name = field.name;
    const text = String(values?.[name] ?? '').trim();
    if (name === 'profile_name') {
      if (text) params.profile_name = text;
      return;
    }
    params[name] = text;
  });
  return params;
}

export function sendBlockers(campaign, values, file) {
  const missing = (campaign?.params || [])
    .map((item) => item.name)
    .filter((name) => name !== 'profile_name')
    .filter((name) => !String(values?.[name] || '').trim());
  const length = finishedMessage(campaign, values).length;
  return {
    missing,
    length,
    tooLong: length > TEMPLATE_CHAR_LIMIT,
    fileRequired: campaign?.header_file === 'required' && !file,
  };
}

export function newIdempotencyKey(leadId, campaignKey) {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `crm:${leadId || 'none'}:${campaignKey}:${id}`;
}

export function shouldRefreshCampaigns(code) {
  return REFRESH_CODES.has(code);
}

const US_TEMPLATE_ORDER = [
  'us 2nd reminder',
  'us 3rd reminder',
  'us demo slot',
  'us pricing',
];

const OTHER_TEMPLATE_ORDER = [
  '2nd reminder',
  '3rd reminder',
  'demo booking slot',
];

function templateSortNames(campaign) {
  const key = String(campaign?.campaign_key || '').trim();
  const rest = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key;
  return [campaign?.label, rest]
    .map((value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function nameMatchesPhrase(name, phrase) {
  return name.startsWith(phrase);
}

function templatePinRank(campaign, plusOne) {
  const names = templateSortNames(campaign);
  const order = plusOne ? US_TEMPLATE_ORDER : OTHER_TEMPLATE_ORDER;
  const index = order.findIndex((phrase) => names.some((name) => nameMatchesPhrase(name, phrase)));
  return index === -1 ? order.length : index;
}

export function sortTemplatesForNumber(campaigns, plusOne) {
  const list = Array.isArray(campaigns) ? campaigns : [];
  return list
    .map((campaign, index) => ({ campaign, index, rank: templatePinRank(campaign, plusOne) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((item) => item.campaign);
}

export function isTimeoutError(err) {
  if (err?.response) return false;
  return err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '') || Boolean(err?.request);
}
